import React, { useState, useEffect } from 'react';
import {
  Row,
  Column,
  Copy,
  Square,
  Flex,
  Button,
  SvgPencil,
  SvgX,
  Squish,
  Stack,
  SvgPlus,
  SvgEnvelope,
} from '@grubhubprod/gh-cookbook-react';
import { ManagePersonModal } from 'src/components/ManagePersonModal/ManagePersonModal';
import Table from 'src/components/Table/Table';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { ROLES } from 'src/types/Employee';
import {
  IDiner,
  ISearchParams,
  SearchStatus,
  LONG_SEARCH_DELAY,
} from 'src/utilities/useSearchHooks';
import { sdk } from 'src/utilities/sdk';
import {
  SortableColumn,
  SortFields,
  SortOrder,
} from 'src/components/Table/SortableColumn';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { pushError, pushSuccess } from 'src/utilities/notifications';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';

interface IManagePeopleModalStatus {
  showModal: boolean;
  dinerId: string;
}

interface IPeopleTableProps {
  diners: IDiner[];
  getCorpDiners: (
    params?: ISearchParams,
    isLoadingMore?: boolean
  ) => Promise<void>;
  hasMoreResults: boolean;
  loadMoreNumber: number;
  corpId: string;
  corpCreatorId?: string;
  isSignupTable?: boolean;
  isLandingTable?: boolean;
  showFooter?: boolean;
  searchStatus: SearchStatus;
  bodyHeight?: string;
  isSortable?: boolean;
  previousSortOrder?: SortOrder;
  previousSortField?: SortFields;
  onSortColumnClick?: (field: SortFields, order: SortOrder) => void;
  showEmptyState?: boolean;
  showNoResultsState?: boolean;
}

export function PeopleTable({
  diners,
  getCorpDiners,
  hasMoreResults,
  loadMoreNumber,
  isSignupTable,
  isLandingTable,
  corpId,
  corpCreatorId,
  searchStatus,
  bodyHeight,
  showFooter = true,
  isSortable,
  previousSortOrder,
  previousSortField,
  onSortColumnClick,
  showEmptyState,
  showNoResultsState,
}: IPeopleTableProps): JSX.Element {
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();

  const [dinerIdDeletionInProgress, setDinerIdDeletionInProgress] =
    useState(null);

  async function handleAsyncStatus() {
    if (asyncTaskStatus === AsyncTaskStatus.COMPLETE) {
      /**
       * The reason for this timeout is that elastic search needs some time to rearrange the indexes
       * after a record is deleted. Without this timeout the search could potentially
       * return outdated results
       */
      setTimeout(() => {
        setDinerIdDeletionInProgress(null);
        const dinerSearchParams: ISearchParams = { page: 1 };

        if (isSignupTable) {
          dinerSearchParams.diner_role = ROLES.DINER;
        }

        getCorpDiners(dinerSearchParams);
        closeRemoveDinerModal();

        pushSuccess('This person has been removed.');
      }, LONG_SEARCH_DELAY);
    } else if (asyncTaskStatus === AsyncTaskStatus.ERROR) {
      setDinerIdDeletionInProgress(null);
      closeRemoveDinerModal();
      // eslint-disable-next-line no-console
      pushError('There was a problem removing this person. Try again.');
    }
  }

  useEffect(() => {
    handleAsyncStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncTaskStatus]);

  const { isDesktop } = useScreenSizes();
  const {
    enterpriseData: { isInternalAdminUser },
  } = useAuth();

  const [sentWelcomeEmailIds, setSentWelcomeEmailIds] = useState<{
    [k: string]: boolean;
  }>({});

  const [managePeopleModalStatus, setManagePeopleModalStatus] =
    useState<IManagePeopleModalStatus>({
      showModal: false,
      dinerId: null,
    });

  const [removeDinerModalStatus, setRemoveDinerModalStatus] =
    useState<IManagePeopleModalStatus>({
      showModal: false,
      dinerId: null,
    });

  const [dinerIdSendingInProgress, setDinerIdSendingInProgress] =
    useState(null);

  const { sendTealiumEvent } = useAnalytics();

  function openManagePeopleModal(dinerId?: string) {
    setManagePeopleModalStatus({
      showModal: true,
      dinerId,
    });
  }

  function closeManagePeopleModal(): void {
    setManagePeopleModalStatus({
      showModal: false,
      dinerId: null,
    });
  }

  function onPersonUpdateSuccess() {
    closeManagePeopleModal();
    const dinerSearchParams: ISearchParams = { page: 1 };

    if (isSignupTable) {
      dinerSearchParams.diner_role = ROLES.DINER;
    }

    getCorpDiners(dinerSearchParams);
  }

  function loadMoreDiners() {
    const dinerSearchParams: ISearchParams = {};

    if (isSignupTable) {
      dinerSearchParams.diner_role = ROLES.DINER;
    }

    getCorpDiners(dinerSearchParams, true);
  }

  async function removeDiner(dinerId: string) {
    try {
      setDinerIdDeletionInProgress(dinerId);

      await startAsyncTask(() => {
        return sdk.http.delete(`/corp-admin/${corpId}/diners/v2/${dinerId}`);
      });
    } catch (error) {
      setDinerIdDeletionInProgress(null);
      closeRemoveDinerModal();
      pushError('There was a problem removing this person. Try again.');
    }
  }

  function onRemoveDiner(dinerId: string) {
    if (isSignupTable) {
      removeDiner(dinerId);
    } else {
      setRemoveDinerModalStatus({
        dinerId,
        showModal: true,
      });
    }
  }

  async function onSendWelcomeEmail(dinerId: string) {
    setDinerIdSendingInProgress(dinerId);
    try {
      await sdk.http.post(
        `/corps/${corpId}/diners/${dinerId}/sendWelcomeEmail`,
        {}
      );
      sendTealiumEvent({
        eventcategory: 'self-service management tool_people',
        eventaction: 'welcome email icon_cta',
        eventlabel: isInternalAdminUser ? 'internal GH admin' : 'client admin',
      });
      setDinerIdSendingInProgress(null);
      setSentWelcomeEmailIds({
        ...sentWelcomeEmailIds,
        [dinerId]: true,
      });
      pushSuccess('Your welcome email was successfully sent.');
    } catch (error) {
      setDinerIdSendingInProgress(null);
      pushError('Your welcome email was not sent. Try again.');
    }
  }

  function closeRemoveDinerModal() {
    setRemoveDinerModalStatus({ showModal: false, dinerId: null });
  }

  function getDinerName(diner: IDiner): JSX.Element {
    return (
      <div className="ellipsisOverflow">
        <Copy variant="body">
          {diner.first_name} {diner.last_name}
        </Copy>
      </div>
    );
  }

  return (
    <>
      <Table
        hideHeader={!isDesktop}
        header={() => (
          <Row gutter={0}>
            {isSortable ? (
              <>
                <Column gutter={0} tablet={4} desktop={3}>
                  <SortableColumn
                    label="Name"
                    previousSortOrder={previousSortOrder}
                    sortField={SortFields.LAST_NAME}
                    previousSortField={previousSortField}
                    onSortColumnClick={onSortColumnClick}
                  />
                </Column>
                <Column gutter={0} tablet={6}>
                  <SortableColumn
                    label="Email"
                    previousSortOrder={previousSortOrder}
                    sortField={SortFields.EMAIL}
                    previousSortField={previousSortField}
                    onSortColumnClick={onSortColumnClick}
                  />
                </Column>
              </>
            ) : (
              <>
                <Column gutter={0} tablet={4} desktop={3}>
                  <Copy variant="body" color="secondaryText">
                    Name
                  </Copy>
                </Column>
                <Column gutter={0} tablet={6}>
                  <Copy variant="body" color="secondaryText">
                    Email
                  </Copy>
                </Column>
              </>
            )}

            <Column gutter={0} tablet={1} />
            <Column gutter={0} tablet={1} />
          </Row>
        )}
        body={() => (
          <ul className="removeListStyles">
            {diners.map((diner) => (
              <Square
                as="li"
                inset="sm"
                key={diner.id}
                borderBottom="1px solid"
                borderColor="lightLine"
              >
                <Row gutter={4} alignItems="center">
                  {isDesktop ? (
                    <>
                      <Column gutter={4} desktop={3}>
                        <Flex alignItems="center">{getDinerName(diner)}</Flex>
                      </Column>
                      <Column gutter={4} desktop={6}>
                        <div className="ellipsisOverflow">
                          <Copy variant="body">{diner.email}</Copy>
                        </div>
                      </Column>
                    </>
                  ) : (
                    <>
                      <Column gutter={0} phone={isLandingTable ? 9 : 10}>
                        {getDinerName(diner)}
                        <div className="ellipsisOverflow">
                          <Copy variant="body">{diner.email}</Copy>
                        </div>
                      </Column>
                    </>
                  )}
                  <Column gutter={0} phone={1}>
                    <Flex justifyContent="center" alignItems="center">
                      {isLandingTable && isDesktop ? (
                        <Button
                          variant="tertiary"
                          buttonSize="medium"
                          className="tableActionButtons"
                          disabled={
                            dinerIdDeletionInProgress === diner.diner_id
                          }
                          onClick={() => openManagePeopleModal(diner.diner_id)}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Tooltip
                          roundedContainer
                          content={
                            <Copy variant="body" color="invertedPrimaryText">
                              Edit
                            </Copy>
                          }
                        >
                          <div>
                            <Button
                              variant="tertiary"
                              className="tableActionButtons"
                              disabled={
                                dinerIdDeletionInProgress === diner.diner_id
                              }
                              onClick={() =>
                                openManagePeopleModal(diner.diner_id)
                              }
                            >
                              <SvgPencil aria-label="Edit" />
                            </Button>
                          </div>
                        </Tooltip>
                      )}
                    </Flex>
                  </Column>

                  <Column gutter={0} phone={1}>
                    {/* WDA-26463 do not allow admin to be removed */}
                    {/* admin is not included in signup table */}
                    {(isSignupTable ||
                      (corpCreatorId && corpCreatorId !== diner.diner_id)) && (
                      <Flex justifyContent="center" alignItems="center">
                        {isLandingTable && isDesktop ? (
                          <Button
                            variant="tertiary"
                            className="tableActionButtons"
                            disabled={
                              dinerIdDeletionInProgress === diner.diner_id
                            }
                            buttonSize="medium"
                            onClick={() => onRemoveDiner(diner.diner_id)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Tooltip
                            roundedContainer
                            content={
                              <Copy variant="body" color="invertedPrimaryText">
                                Delete
                              </Copy>
                            }
                          >
                            <div>
                              <Button
                                variant="tertiary"
                                className="tableActionButtons"
                                isLoading={
                                  dinerIdDeletionInProgress === diner.diner_id
                                }
                                onClick={() => onRemoveDiner(diner.diner_id)}
                              >
                                <SvgX aria-label="Remove" />
                              </Button>
                            </div>
                          </Tooltip>
                        )}
                      </Flex>
                    )}
                  </Column>

                  {isLandingTable && (
                    <Column gutter={0} phone={1}>
                      <Flex justifyContent="center">
                        <Tooltip
                          disabled={sentWelcomeEmailIds[diner.diner_id]}
                          roundedContainer
                          content={
                            <Copy variant="body" color="invertedPrimaryText">
                              Send Welcome Email
                            </Copy>
                          }
                        >
                          <div>
                            <Button
                              disabled={sentWelcomeEmailIds[diner.diner_id]}
                              variant="tertiary"
                              isLoading={
                                dinerIdSendingInProgress === diner.diner_id
                              }
                              className="tableActionButtons"
                              onClick={() => onSendWelcomeEmail(diner.diner_id)}
                            >
                              <SvgEnvelope aria-label="sendWelcomeEmail" />
                            </Button>
                          </div>
                        </Tooltip>
                      </Flex>
                    </Column>
                  )}
                </Row>
              </Square>
            ))}
            {showEmptyState && diners.length === 0 && (
              <Square inset="lg">
                <Flex alignItems="center" justifyContent="center">
                  <Copy variant="body" color="primaryText">
                    Start adding people so they can use their line of credit.
                  </Copy>
                </Flex>
              </Square>
            )}
            {showNoResultsState && diners.length === 0 && (
              <Square inset="lg">
                <Flex alignItems="center" justifyContent="center">
                  <Copy variant="body" color="primaryText">
                    We couldn’t find who you were looking for. Give it another
                    go.
                  </Copy>
                </Flex>
              </Square>
            )}
            {hasMoreResults && (
              <Squish inset="lg">
                <Button
                  variant="secondary"
                  isLoading={searchStatus === SearchStatus.LOADING}
                  block
                  onClick={loadMoreDiners}
                >
                  {searchStatus === SearchStatus.COMPLETE && (
                    <span>Load {loadMoreNumber} more</span>
                  )}
                  {searchStatus === SearchStatus.ERROR && (
                    <>
                      <Copy variant="body" fontFamily="default">
                        Something went wrong loading your results.{' '}
                        <Copy variant="body" color="interactive">
                          Try again
                        </Copy>
                      </Copy>
                    </>
                  )}
                </Button>
              </Squish>
            )}
          </ul>
        )}
        isSignupOrSetupTable={isSignupTable}
        showFooter={showFooter}
        bodyMinHeight={!isDesktop ? null : bodyHeight}
        bodyMaxHeight={!isDesktop ? null : bodyHeight}
        justifyFooter="flex-start"
        footerMinHeight="auto"
        footerBg="tintedBackground"
        footer={() => (
          <Square inset="x-sm">
            <Button variant="tertiary" onClick={() => openManagePeopleModal()}>
              <Flex alignItems="center">
                <Stack x="x-sm">
                  <SvgPlus />
                </Stack>
                <Copy variant="body" color="interactive">
                  Add a new person
                </Copy>
              </Flex>
            </Button>
          </Square>
        )}
      />
      {managePeopleModalStatus.showModal && (
        <ManagePersonModal
          corpId={corpId}
          closeModal={closeManagePeopleModal}
          dinerId={managePeopleModalStatus.dinerId}
          onSuccess={onPersonUpdateSuccess}
        />
      )}

      {removeDinerModalStatus.showModal && (
        <SimpleDialog
          id={removeDinerModalStatus.dinerId}
          primaryActionFlavor="danger"
          title="Remove this person?"
          description="This person will no longer be able to use any lines of credit."
          primaryActionText="Remove"
          isPrimaryButtonLoading={dinerIdDeletionInProgress !== null}
          buttonsDirection="row-reverse"
          secondaryActionText="Cancel"
          disablePrimaryActionButton={dinerIdDeletionInProgress !== null}
          disableSecondaryActionButton={dinerIdDeletionInProgress !== null}
          onSecondaryActionClick={closeRemoveDinerModal}
          onPrimaryActionClick={(id) => removeDiner(id)}
        />
      )}
    </>
  );
}
